import { Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import {firstValueFrom, forkJoin} from 'rxjs';
import { first } from 'rxjs/operators';

import { UICollectionService } from '@sim/collections/services';
import { RoleService } from '@sim/role/services/role.service';
import {
  BaseTableFilteredComponent,
  basicDeleteTemplateOptions,
  basicEditTemplateOptions,
  basicShowTemplateOptions,
  TableContextMenu,
  TableField,
  TableFilterType,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import { CardsApiService, filePath, SwalService, UIExportService } from '@atmi/core';
import { cardF } from '@atmi/core/models';
import { SettingsApiService } from '@atmi/core/services/settings-api.service';
import { CardFFiltersComponent } from '@atmi/shared/filters/card-f-filters/card-f-filters.component';
import { IndexQueryStringType } from '@src/app/core/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-cards-f',
  styleUrls: ['./cards-f.component.scss'],
  templateUrl: './cards-f.component.html',
})
export class CardsFComponent extends BaseTableFilteredComponent<cardF.CardF, cardF.CardFFilter> implements UiTableComponent<cardF.CardF> {

  readonly MobileFiltersComponent = CardFFiltersComponent;

  readonly fields: TableField<cardF.CardF>[] = [
    {
      header: '',
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'chekbox',
      selectable: true,
    },
    {
      context: (x) => x.previewUrl ? `${this.fileUrl}${x.previewUrl}` : undefined,
      header: this.translate.instant('Anteprima'),
      key: 'previewUrl',
      template: 'img',
    },
    {
      context: (x) => x.SGT.SGTT,
      header: this.translate.instant('Titolo soggetto'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'SGT.SGTT',
      sortable: true,
    },
    {
      header: this.translate.instant('Identificazione'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'SGT.SGTI',
      sortable: true,
    },
    {
      context: (x) => x.OGT.OGTD,
      header: this.translate.instant('Oggetto'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'OGT.OGTD',
      sortable: true,
    },
    {
      header: this.translate.instant('Occasione'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'LRO',
      sortable: true,
    },
    {
      context: (x) => x.DT.length ? x.DT[0].DTS.DTSI : '-',
      header: this.translate.instant('Anno'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'DT.DTS.DTSI',
      sortable: true,
    },
    {
      align: 'right',
      header: '',
      key: 'context',
      showBreakpoints: [Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium],
      template: 'context',
    },
  ];

  currentFilters: IndexQueryStringType = {};

  readonly pager$ = ((page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) => {
    sort = sort && sort.length > 0 ? sort : [{ field: 'SGT.SGTT', type: 'asc' }];
    this.currentFilters = { sort, filter };
    return this.cardsApi.indexF({ sort, page, limit, filter });
  });

  readonly contextMenus: Array<TableContextMenu<cardF.CardF>> = [];

  readonly isExportEnable = this.settingsApi.selectStateSnapshot().system?.layouts.some((l) => l.className === 'BeneF');

  defaultLimit = 300;

  constructor(
    @Inject(filePath) private readonly fileUrl: string,
    private readonly cardsApi: CardsApiService,
    private readonly roleService: RoleService,
    private readonly settingsApi: SettingsApiService,
    private readonly uiExportService: UIExportService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly swalService: SwalService,
    protected readonly translate: TranslateService,
    readonly uiCollectionService: UICollectionService,
  ) {
    super(translate, router, route, cardF.CardFFiltersMeta, swalService, 'f/manager');

    if (this.roleService.hasCapability('BeneF', ['update'])) {
      this.contextMenus.push({
        action: (item) => this.editShowHandler(item),
        label: this.translate.instant('Modifica'),
      });
      this.fields.push(basicEditTemplateOptions);
    } else if (this.roleService.hasCapability('BeneF', ['read', 'read+all'])) {
      this.contextMenus.push({
        action: (item) => this.editShowHandler(item),
        label: this.translate.instant('Visualizza'),
      });
      this.fields.push(basicShowTemplateOptions);
    }

    if (this.roleService.hasCapability('BeneF', ['delete'])) {
      this.contextMenus.push({
        action: (item) => item.id ? this.deleteHandler(item.id) : () => undefined,
        label: this.translate.instant('Elimina'),
      });
      this.fields.push(basicDeleteTemplateOptions);
    }
  }

  deleteConcreteHandler(id: string) {
    return this.cardsApi.removeF(id);
  }

  deleteSelectedConcreteHandler(ids: string[]) {
    const deleteApis = ids.map((id) => this.cardsApi.removeF(id));
    return forkJoin(deleteApis);
  }

  async addToCollectionHandler() {
    const success = await this.uiCollectionService.showCollectionPickerHandler(this.selectedItemIds, 'BeneF');
    if (success) {
      this.selectedItems = [];
    }
  }

  async exportHandler() {
    this.uiExportService.manageExport(
      async (template?: string, format?: string) => {
        const selection = this.selectedItemIds.length ? this.selectedItemIds : undefined;
        const blob = await firstValueFrom(this.cardsApi.exportList('f', template, format, this.currentFilters, selection).pipe(first()));
        this.selectedItems = [];
        return blob;
      },
      'BeneF',
      `beni-f`,
    );
  }
}
